import {FormAction, FormState} from '@raison/form-models';
import {useLocationState} from '@raison/navigation';
import {RsnButton, RsnDialog, RsnTextButton, styles, useBoolean} from '@raison/ui-kit';
import * as Schema from 'effect/Schema';
import React, {useEffect, useMemo, useReducer, useState} from 'react';
import {useNavigate} from 'react-router';
import {
    CaptchaErrorDialog,
    CaptchaUsageMention,
    isCaptchaError,
    shouldThrowCaptchaError,
    useCaptcha,
} from '~/entities/captcha';
import {CaptchaError} from '~/entities/captcha/api/useCaptcha';
import {GenericErrorMsg} from '~/entities/dialogs';
import {selectUserIsAuthorizedFlag, useVerificationMethodsMutation} from '~/entities/user';
import {
    FallbackVerificationDialog,
    InvalidCodeDialog,
    SmsErrorDialog,
    SmsErrorStatus,
    TwoFaCodeForm,
    useResendTimer,
    useSmsStatusPolling,
    VerificationMethodDialog,
    VerificationMethodType,
} from '~/features/two-factor';
import {
    ForgotPasswordStartErrorCode,
    ForgotPasswordVerifyCodeErrorCode,
    useForgotPasswordStartMutation,
    useForgotPasswordVerifyCodeMutation,
} from '~/processes/onboarding/api/forgot-password/forgotPassword';
import {PasswordRecoverySubmittedDialog} from '~/processes/onboarding/ui/forgot-password/PasswordRecoverySubmittedDialog';
import {CouldNotSendDialog} from '~/processes/onboarding/ui/shared/CouldNotSendDialog';
import {TooManyWrongCodesDialog} from '~/processes/onboarding/ui/shared/TooManyWrongCodesDialog';
import {TooManyFailedAttemptsDialog} from '~/processes/onboarding/ui/signup/TooManyFailedAttemptsDialog';
import {appLocalStorage} from '~/shared/persistence';
import {useAppDispatch, useAppSelector} from '~/shared/store';
import {Username} from '../../model/Username';
import {CodeAlreadySent} from '../shared/CodeAlreadySent';

export type PhoneVerificationScreenProps = {
    onVerificationSuccess: (values: {username: string; uuid: string}) => void;
    isOTP: boolean;
    goToOTP: () => void;
};

export function PhoneVerificationScreen({
    onVerificationSuccess,
    isOTP,
    goToOTP: goToOTPExternal,
}: PhoneVerificationScreenProps) {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const isAuthorized = useAppSelector(selectUserIsAuthorizedFlag);

    const savedUsername = useMemo(() => {
        const saved = appLocalStorage.username.get();
        if (saved) {
            return Username.fromString(saved);
        }
        throw new Error('Not expected to be called w/o username saved');
    }, []);

    const [dialog, openDialog] = useLocationState('forgot-pass-phone-verification-screen-dialog', Dialog);
    const closeDialog = () => {
        navigate(-1);
    };

    const [form, dispatchForm] = useReducer(
        FormState.reduce<SignupStartValues>,
        {
            code: '',
        },
        FormState.from
    );

    const [captchaError, setCaptchaError] = useState<CaptchaError>();
    const [smsErrorType, setSmsErrorType] = useState<SmsErrorStatus>();
    const [retryAfterTs, setRetryAfterTs] = useState(Date.now());
    const [isSubmitting, setIsSubmitting] = useBoolean(false);
    const [isResending, setIsResending] = useBoolean(false);

    const {
        mutateAsync: fetchVerificationMethods,
        data: {value: verificationMethods = []} = {},
        isPending: isVerificationMethodsPending,
    } = useVerificationMethodsMutation();

    const {mutateAsync: forgotPasswordStart} = useForgotPasswordStartMutation();
    const {mutateAsync: verifyCode, isPending: isVerificationPending} = useForgotPasswordVerifyCodeMutation();

    const captcha = useCaptcha();
    const [timer$, timerActions] = useResendTimer();
    const {startPolling} = useSmsStatusPolling();

    function resetCode() {
        dispatchForm(
            FormAction.Update(form => {
                form.fields.code.value = '';
                form.fields.code.wasModified = Date.now();
            })
        );
    }

    const goToOTP = () => {
        resetCode();
        goToOTPExternal();
    };

    useEffect(() => {
        // The default timer state is `pending`, which renders nothing.
        // Switch it to success state with 0 seconds timer to immediately show the "Resend code" button.
        timerActions.success(0);
    }, []);

    const requestCode = async (options: {username: string; resending?: boolean; method?: VerificationMethodType}) => {
        const resending = options.resending ?? false;
        const verificationMethod = options?.method;
        const username = options?.username;

        appLocalStorage.username.set(username);

        try {
            const captchaToken = await captcha.execute();

            const {value, problem} = await forgotPasswordStart({
                username,
                repeated_event: resending ? 'yes' : 'no',
                verificationMethod,
                captchaToken,
            }).catch(handleError);

            if (problem) {
                switch (problem.error_code) {
                    case ForgotPasswordStartErrorCode.notFound: {
                        openDialog(Dialogs.couldNotSend);
                        return;
                    }
                    case ForgotPasswordStartErrorCode.cooldownPeriod: {
                        timerActions.success(problem.retry_in);
                        openDialog(Dialogs.codeAlreadySent);
                        return;
                    }
                    case ForgotPasswordStartErrorCode.requestLimit: {
                        setRetryAfterTs(Date.now() + problem.retry_in * 1000);
                        openDialog(Dialogs.tooManyAttempts);
                        return;
                    }
                    default: {
                        dispatch(GenericErrorMsg.open());
                        return;
                    }
                }
            }

            if (value.code === 1) {
                openDialog(Dialogs.alreadySubmitted);
                return;
            }

            const tenantId = value.tenant_uuid;
            timerActions.success(value.retry_in);
            startPolling({
                onError(errorType) {
                    timerActions.success(0);
                    setSmsErrorType(errorType);
                    openDialog(Dialogs.smsError);
                },
                onSuccess() {
                    if (resending) {
                        openDialog(Dialogs.fallbackVerification);
                    }
                },
                tenantId,
            });

            if (!isOTP) {
                goToOTP();
            }
        } catch (e) {
            if (isCaptchaError(e)) {
                setCaptchaError(e);
                openDialog(Dialogs.captchaError);
                if (shouldThrowCaptchaError(e)) throw e;
            } else {
                dispatch(GenericErrorMsg.open());
                throw e;
            }
        }
    };

    const {fields, onSubmit: handleSubmit} = FormState.selectFormView(
        {...form, issues: {}},
        {
            dispatch: dispatchForm,
            submit: async () => {
                const code = form.fields.code.value;
                const username = savedUsername.toString();

                if (!isOTP) {
                    const {problem, value} = await fetchVerificationMethods(username).catch(handleError);

                    if (problem) {
                        openDialog(Dialogs.couldNotSend);
                        return;
                    }

                    if (value?.length <= 1) {
                        setIsSubmitting.on();
                        requestCode({
                            username,
                            method: verificationMethods?.[0],
                        }).finally(setIsSubmitting.off);
                        return;
                    }

                    openDialog(Dialogs.verificationMethod);
                } else {
                    const {value: uuid, problem} = await verifyCode({
                        username,
                        code,
                    });

                    if (problem) {
                        switch (problem.error_code) {
                            case ForgotPasswordVerifyCodeErrorCode.codeExpired: {
                                return {
                                    code: 'This code has expired',
                                };
                            }
                            case ForgotPasswordVerifyCodeErrorCode.limitReached: {
                                openDialog(Dialogs.tooManyWrongCodes);
                                return;
                            }
                            case ForgotPasswordVerifyCodeErrorCode.wrongCode: {
                                openDialog(Dialogs.invalidCode);
                                return;
                            }
                            case ForgotPasswordVerifyCodeErrorCode.validationFailed: {
                                return problem.issues;
                            }
                            default: {
                                dispatch(GenericErrorMsg.open());
                                return;
                            }
                        }
                    }

                    onVerificationSuccess({
                        username,
                        uuid,
                    });
                }
                return true;
            },
        }
    );

    const handleVerificationMethodChange = (method: VerificationMethodType) => {
        setIsSubmitting.on();
        requestCode({
            username: savedUsername.toString(),
            method,
        }).finally(setIsSubmitting.off);
    };

    function handleRetryIntent() {
        resetCode();
        setIsResending.on();
        requestCode({
            username: savedUsername.toString(),
            resending: true,
        }).finally(setIsResending.off);
    }

    const handleError = (e: unknown) => {
        dispatch(GenericErrorMsg.open());
        throw e;
    };

    function renderScreen() {
        if (isOTP) {
            return (
                <div className={'p-4'}>
                    <TwoFaCodeForm
                        timer$={timer$}
                        onRetryIntent={handleRetryIntent}
                        otp={fields.code.value}
                        onOtpChange={fields.code.onChange}
                        onInputComplete={() => handleSubmit()}
                        isPending={isResending || isVerificationPending}
                        issue={fields.code.issue}
                        heading={
                            <hgroup className={cls.header}>
                                <h2 className={cls.title}>Phone number verification</h2>
                                <small className={cls.subtitle}>
                                    Enter the one-time code we just sent <br />
                                    to <span className={'fv-text-l-bold'}>{savedUsername.formatted}</span>
                                </small>
                            </hgroup>
                        }
                        deliveryInfo={null}
                    />
                </div>
            );
        }

        return (
            <form className={'flex flex-1 flex-col'} onSubmit={handleSubmit}>
                <div className={'flex flex-1 flex-col gap-2 p-4'}>
                    <div className={'fv-text-l-regular text-neutral-90'}>
                        Password recovery for phone number <br />
                        <span className={'fv-text-l-bold'}>{savedUsername.formatted}</span>.
                    </div>
                    {!isAuthorized && (
                        <RsnTextButton.Inline size={'l'} onClick={() => navigate('/login', {replace: true})}>
                            Change phone number
                        </RsnTextButton.Inline>
                    )}
                </div>
                <div className={'flex flex-col gap-4 p-4'}>
                    <div className={'flex flex-col gap-2'}>
                        <RsnButton.Primary type={'submit'} disabled={isSubmitting || isVerificationMethodsPending}>
                            Next
                        </RsnButton.Primary>
                    </div>
                </div>
            </form>
        );
    }

    return (
        <>
            {renderScreen()}
            <CaptchaUsageMention />
            <RsnDialog.Controller
                onClose={closeDialog}
                onExitEnd={() => {
                    if (isOTP) {
                        resetCode();
                        focusOTPInput();
                    }
                }}
            >
                {(() => {
                    switch (true) {
                        case dialog === Dialogs.alreadySubmitted: {
                            return <PasswordRecoverySubmittedDialog />;
                        }
                        case dialog === Dialogs.verificationMethod: {
                            return (
                                <VerificationMethodDialog
                                    methods={verificationMethods || []}
                                    onChange={handleVerificationMethodChange}
                                />
                            );
                        }
                        case dialog === Dialogs.smsError && !!smsErrorType: {
                            return <SmsErrorDialog type={smsErrorType} />;
                        }
                        case dialog === Dialogs.fallbackVerification: {
                            return <FallbackVerificationDialog />;
                        }
                        case dialog === Dialogs.invalidCode: {
                            return <InvalidCodeDialog />;
                        }
                        case dialog === Dialogs.tooManyAttempts: {
                            return <TooManyFailedAttemptsDialog retryAfterTs={retryAfterTs} />;
                        }
                        case dialog === Dialogs.tooManyWrongCodes: {
                            return <TooManyWrongCodesDialog />;
                        }
                        case dialog === Dialogs.captchaError && !!captchaError: {
                            return <CaptchaErrorDialog error={captchaError} />;
                        }
                        case dialog === Dialogs.couldNotSend: {
                            return <CouldNotSendDialog onAnotherNumber={() => navigate(-2)} />;
                        }
                    }
                })()}
            </RsnDialog.Controller>
            <RsnDialog.Controller onClose={closeDialog} onExitEnd={goToOTP}>
                {dialog === Dialogs.codeAlreadySent && <CodeAlreadySent />}
            </RsnDialog.Controller>
        </>
    );
}

const cls = styles({
    link: 'fv-text-l-bold text-primary-30',
    header: 'flex flex-col gap-2 pt-2',
    title: 'fv-heading-l text-neutral-100',
    subtitle: 'fv-text-l-regular text-neutral-90',
});

export type SignupStartValues = {
    code: string;
};

function focusOTPInput() {
    TwoFaCodeForm.focusOTPInput();
}
const Dialogs = {
    smsError: 'sms-error',
    verificationMethod: 'verification-method',
    fallbackVerification: 'fallback-verification',
    codeAlreadySent: 'code-already-sent',
    couldNotSend: 'could-not-send',
    invalidCode: 'invalid-code',
    tooManyAttempts: 'too-many-attempts',
    tooManyWrongCodes: 'too-many-wrong codes',
    captchaError: 'captcha-error',
    alreadySubmitted: 'already-submitted',
} as const;
const Dialog = Schema.Literal(...Object.values(Dialogs));
